//region :: Imports ::
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import React, {createRef, Fragment, ReactElement, RefObject} from 'react';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MButton from '../../Toolkit/Components/Controls/Button';
import {CloseIconName, NavigateToIconName, SaveIconName, SuccessIcon} from '../../Toolkit/Icons';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import withRouter from '../../Toolkit/WithRouter';
import WorkerService from '../../Services/WorkerService';
import {Worker, WorkerValidator} from '../../Services/WorkerHubTypes';
import {FieldProps, FormikHelpers} from 'formik';
import {AddWorkerToNhsRequest, IAddWorkerToNhsRequest} from '../../Api/WorkerHubClients';
import {copyToClipboard, MNullable, toErrorMessage} from '../../Toolkit/Utils';
import MForm, {MFormOutcome} from '../../Toolkit/Components/Controls/Form';
import MAlertDialog from '../../Toolkit/Components/Controls/AlertDialog';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import MFormFieldControl from '../../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import MCopyButton from '../../Toolkit/Components/Controls/CopyButton';
import * as Yup from 'yup';
import MCalendar from '../../Toolkit/Components/Controls/Calendar';
import MDynEntityLookup from '../../Toolkit/Components/Controls/Dynamics/DynEntityLookup';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import WorkerNhsService from '../../Services/WorkerNhsService';

//endregion

/** */
type RouteParams = {
    workerId: string;
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type FormData = {
    workerId: string;
    firstName: string;
    lastName: string;
    startDate: MNullable<Date>
    organizationUnit: string;
    resourceManager: string;
}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    worker: MNullable<Worker>,
    form: {
        id: string;
        data: FormData;
        isSubmitting: boolean;
        validationSchema: any;
    }
}

/** */
class AddWorkerToNhsPage extends MComponent<Props, State> {

    private readonly m_nhsUrl: string;
    private readonly m_workerService: WorkerService;
    private readonly m_workerNhsService: WorkerNhsService;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_alertDialogRef: RefObject<MAlertDialog>;
    private readonly m_formRef: RefObject<MForm<FormData>>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethodS();

        this.m_nhsUrl = process.env.REACT_APP_D365_NHS_HOST_URL!;
        this.m_workerService = new WorkerService(this.props.msalContext);
        this.m_workerNhsService = new WorkerNhsService(this.props.msalContext);
        this.m_pageRef = createRef<MPage>();
        this.m_alertDialogRef = createRef<MAlertDialog>();
        this.m_formRef = createRef<MForm<FormData>>();

        this.initializeState();

    }

    /** */
    private bindMethodS(): void {

        this.isFormDisabled = this.isFormDisabled.bind(this);
        this.isSaveButtonDisabled = this.isSaveButtonDisabled.bind(this);
        this.isCloseButtonDisabled = this.isCloseButtonDisabled.bind(this);
        this.isStartDateDisabled = this.isStartDateDisabled.bind(this);
        this.isOrganizationUnitDisabled = this.isOrganizationUnitDisabled.bind(this);
        this.isResourceManagerDisabled = this.isResourceManagerDisabled.bind(this);
        this.closeButton_onClick = this.closeButton_onClick.bind(this);
        this.navigateToWorkerButton_onClick = this.navigateToWorkerButton_onClick.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);

    }

    /** */
    private initializeState(): void {

        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: {
                locked: false
            },
            worker: null,
            form: {
                id: 'mainForm',
                data: {
                    workerId: '',
                    firstName: '',
                    lastName: '',
                    startDate: null,
                    organizationUnit: '',
                    resourceManager: ''
                },
                isSubmitting: false,
                validationSchema: Yup.object().shape({
                    startDate: WorkerValidator.startDate,
                    organizationUnit: WorkerValidator.organizationUnit,
                    resourceManager: WorkerValidator.resourceManager
                })
            }
        };

    }

    /** */
    private showWorkerAddedDialog(): void {

        this.m_alertDialogRef.current!.show(
            'Add Worker to System',
            `${this.state.worker!.firstName} ${this.state.worker!.lastName} has been added to D365 NHS.`,
            SuccessIcon,
            <MButton label="Ok"
                     color="success"
                     icon={NavigateToIconName}
                     disabled={false}
                     onClick={this.navigateToWorkerButton_onClick}/>
        );

    }

    /** */
    private async navigateToWorker(): Promise<void> {
        await this.props.history.goBack();
    }

    /** */
    private isFormDisabled(): boolean {
        return this.state.form.isSubmitting;
    }

    /** */
    private isSaveButtonDisabled(): boolean {
        return this.isFormDisabled() || (this.state.worker !== null && this.state.worker.existsInNhs);
    }

    /** */
    private isCloseButtonDisabled(): boolean {
        return this.isFormDisabled();
    }

    /** */
    private isStartDateDisabled(): boolean {
        return this.isFormDisabled() || (this.state.worker !== null && this.state.worker.existsInNhs);
    }

    /** */
    private isOrganizationUnitDisabled(): boolean {
        return this.isFormDisabled() || (this.state.worker !== null && this.state.worker.existsInNhs);
    }

    /** */
    private isResourceManagerDisabled(): boolean {
        return this.isFormDisabled() || (this.state.worker !== null && this.state.worker.existsInNhs);
    }

    /** */
    private async closeButton_onClick(): Promise<void> {
        await this.navigateToWorker();
    }

    /** */
    private async navigateToWorkerButton_onClick(): Promise<void> {
        await this.navigateToWorker();
    }

    /** */
    private form_onBeforeSubmit(fieldValues: FormData, formikHelpers: FormikHelpers<FormData>): boolean {

        const cancel = false;
        this.m_pageRef.current!.messageBar.close();
        this.safeSetState((draft: State) => draft.form.isSubmitting = true);
        return cancel;

    }

    /** */
    private async form_onSubmit(fieldValues: FormData, formikHelpers: FormikHelpers<FormData>): Promise<void> {

        const result = await this.m_workerNhsService.addWorker(
            fieldValues.workerId,
            new AddWorkerToNhsRequest({
                startDate: fieldValues.startDate,
                organizationUnitName: fieldValues.organizationUnit,
                resourceManagerName: fieldValues.resourceManager
            } as IAddWorkerToNhsRequest));

        if(result.hasErrors) {
            throw result.exception;
        }

    }

    /** */
    private form_onError(exception: any): boolean {

        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;

    }

    /** */
    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: FormData, formikHelpers: FormikHelpers<FormData>, exception: any | undefined): void {

        if(outcome === 'success') {

            this.showWorkerAddedDialog();
            return;

        }

        this.safeSetState(
            (draft: State) => draft.form.isSubmitting = false);

    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton formId={this.state.form.id}
                         type="submit"
                         color="success"
                         icon={SaveIconName}
                         iconOnly={true}
                         isBusy={this.isFormDisabled}
                         disabled={this.isSaveButtonDisabled}/>
                <MSpacer/>
                <MButton label="Close"
                         icon={CloseIconName}
                         disabled={this.isCloseButtonDisabled}
                         onClick={this.closeButton_onClick}/>
            </MToolbar>
        );

    }

    /** */
    private async page_onLoadData(): Promise<void> {

        const workerId = this.props.match.params.workerId;
        const result = await this.m_workerService.get(workerId);

        if(result.hasErrors) {
            throw result.exception;
        }

        const reply = result.reply!;
        const worker = reply.worker!;

        this.safeSetState((draft: State) => {

            draft.pageTitle = 'Add worker to D365 NHS';
            draft.pageSubtitle = '';
            draft.worker = worker;
            draft.form.data = {
                workerId: worker.id,
                firstName: worker.firstName,
                lastName: worker.lastName,
                startDate: null,
                organizationUnit: '',
                resourceManager: ''
            };

        });

        if(worker.existsInNhs) {

            this.m_pageRef.current!.messageBar.warning(
                `${worker.firstName} ${worker.lastName} has already been added to D365 NHS`, true, false);

        }

    }

    /** */
    public render(): ReactElement {

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={8}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MAlertDialog ref={this.m_alertDialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.data}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onError={this.form_onError}
                       onAfterSubmit={this.form_onAfterSubmit}
                       ref={this.m_formRef}>

                    <MFormSection title="General">

                        {/* Worker ID */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Worker ID"
                                               fieldName="workerId">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <Fragment>

                                            <MTextBox id={fieldProps.field.name}
                                                      maxLength={4}
                                                      fieldProps={fieldProps}
                                                      disabled={true}
                                                      className={fieldClasses}/>

                                            <MCopyButton disabled={this.isFormDisabled}
                                                         onClick={async() => await copyToClipboard(this.state.form.data.workerId)}/>

                                        </Fragment>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Padding */}
                        <div className="col-12 md:col-8"/>

                        {/* First Name */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="First Name"
                                               fieldName="firstName">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <Fragment>

                                            <MTextBox id={fieldProps.field.name}
                                                      maxLength={100}
                                                      fieldProps={fieldProps}
                                                      disabled={true}
                                                      className={fieldClasses}/>

                                            <MCopyButton disabled={this.isFormDisabled}
                                                         onClick={async() => await copyToClipboard(this.state.form.data.firstName)}/>

                                        </Fragment>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Last Name */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="LastName"
                                               fieldName="lastName">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <Fragment>

                                            <MTextBox id={fieldProps.field.name}
                                                      maxLength={100}
                                                      fieldProps={fieldProps}
                                                      disabled={true}
                                                      className={fieldClasses}/>

                                            <MCopyButton disabled={this.isFormDisabled}
                                                         onClick={async() => await copyToClipboard(this.state.form.data.lastName)}/>

                                        </Fragment>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                    </MFormSection>

                    <MFormSection title="Bookable Resource">

                        {/* Start Date */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Start Date"
                                               fieldName="startDate">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MCalendar id={fieldProps.field.name}
                                                   fieldProps={fieldProps}
                                                   disabled={this.isStartDateDisabled}
                                                   className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Organization Unit */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Organisation Unit"
                                               fieldName="organizationUnit">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_nhsUrl}
                                                          entityLogicalName="msdyn_organizationalunit"
                                                          searchFieldName="msdyn_name"
                                                          searchFilter="statuscode eq 1"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isOrganizationUnitDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Resource Manager */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Resource Manager"
                                               fieldName="resourceManager">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_nhsUrl}
                                                          entityLogicalName="bookableresource"
                                                          searchFieldName="name"
                                                          searchFilter="statuscode eq 1"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isResourceManagerDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                    </MFormSection>
                </MForm>

            </MPage>
        );

    }

}

export default withMsal(withRouter(AddWorkerToNhsPage));
